import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import {
  LocationOn,
  Schedule,
  Phone,
  Email,
  Language,
  Launch,
} from "@mui/icons-material";
import { gradients } from "../../utils/theme";

interface MallDetailsProps {
  location?: string;
  city?: string;
  state?: string;
  pincode?: string;
  openingHours?: string;
  operatingDays?: string;
  contactNumber?: string;
  email?: string;
  website?: string;
}

/**
 * MallDetails Component
 *
 * Compact card displaying mall contact information in a grid layout:
 * - Physical address with city/state/pincode
 * - Operating hours
 * - Contact details (phone, website)
 *
 * Features glass-morphism design with gradient accent and compact spacing.
 */
const MallDetails: React.FC<MallDetailsProps> = ({
  location,
  city,
  state,
  pincode,
  openingHours,
  operatingDays,
  contactNumber,
  email,
  website,
}) => {
  const theme = useTheme();

  // Format full address
  const fullAddress = [location, city, state, pincode]
    .filter(Boolean)
    .join(", ");

  // Display opening hours as-is (single string format)
  const displayHours = openingHours || "Not available";

  return (
    <Card
      sx={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.95
        )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backdropFilter: "blur(10px)",
        boxShadow: 6,
        minHeight: { xs: "auto", md: 600 },
        display: "flex",
        flexDirection: "column",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gradients.navyLux,
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, md: 2 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ flex: 1 }}>
          {/* Address */}
          {fullAddress && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, md: 44 },
                    height: { xs: 40, md: 44 },
                    borderRadius: 2,
                    background: gradients.navyLux,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.primary.main,
                      0.3
                    )}`,
                  }}
                >
                  <LocationOn sx={{ fontSize: { xs: 20, md: 22 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontSize: "0.75rem",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Location Address
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 500,
                      lineHeight: 1.6,
                      fontSize: { xs: "0.95rem", md: "1rem" },
                    }}
                  >
                    {fullAddress}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Operating Hours */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                p: { xs: 1.5, md: 2 },
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.info.main,
                  0.05
                )} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 12px ${alpha(
                    theme.palette.info.main,
                    0.15
                  )}`,
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, md: 44 },
                  height: { xs: 40, md: 44 },
                  borderRadius: 2,
                  background: gradients.royalBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  flexShrink: 0,
                  boxShadow: `0 4px 12px ${alpha(
                    theme.palette.info.main,
                    0.3
                  )}`,
                }}
              >
                <Schedule sx={{ fontSize: { xs: 20, md: 22 } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: "0.75rem",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Operating Hours
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.primary",
                    fontWeight: 700,
                    mb: 1.5,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                  }}
                >
                  {displayHours}
                </Typography>
                {operatingDays && (
                  <Chip
                    label={operatingDays}
                    sx={{
                      height: 32,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      background: gradients.royalBlue,
                      color: "white",
                      border: "none",
                      px: 1,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Grid>

          {/* Phone */}
          {contactNumber && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.error.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.error.main,
                      0.15
                    )}`,
                    "& .action-icon": {
                      bgcolor: alpha(theme.palette.error.main, 0.15),
                      transform: "rotate(-5deg)",
                    },
                  },
                }}
                onClick={() => window.open(`tel:${contactNumber}`, "_self")}
              >
                <Box
                  sx={{
                    width: { xs: 40, md: 44 },
                    height: { xs: 40, md: 44 },
                    borderRadius: 2,
                    background: gradients.coral,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.error.main,
                      0.3
                    )}`,
                  }}
                >
                  <Phone sx={{ fontSize: { xs: 18, md: 20 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Phone
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                    }}
                  >
                    {contactNumber}
                  </Typography>
                </Box>
                <IconButton
                  className="action-icon"
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    transition: "all 0.3s ease",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Launch sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Grid>
          )}

          {/* Website */}
          {website && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.success.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.success.main,
                      0.15
                    )}`,
                    "& .action-icon": {
                      bgcolor: alpha(theme.palette.success.main, 0.15),
                      transform: "rotate(-5deg)",
                    },
                  },
                }}
                onClick={() => window.open(website, "_blank")}
              >
                <Box
                  sx={{
                    width: { xs: 40, md: 44 },
                    height: { xs: 40, md: 44 },
                    borderRadius: 2,
                    background: gradients.emerald,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.success.main,
                      0.3
                    )}`,
                  }}
                >
                  <Language sx={{ fontSize: { xs: 18, md: 20 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Website
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {website.replace(/^https?:\/\//, "")}
                  </Typography>
                </Box>
                <IconButton
                  className="action-icon"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    transition: "all 0.3s ease",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Launch sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Grid>
          )}

          {/* Email */}
          {email && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.secondary.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(
                    theme.palette.secondary.main,
                    0.1
                  )}`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.secondary.main,
                      0.15
                    )}`,
                    "& .action-icon": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.15),
                      transform: "rotate(-5deg)",
                    },
                  },
                }}
                onClick={() => window.open(`mailto:${email}`, "_self")}
              >
                <Box
                  sx={{
                    width: { xs: 40, md: 44 },
                    height: { xs: 40, md: 44 },
                    borderRadius: 2,
                    background: gradients.gold,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.secondary.main,
                      0.3
                    )}`,
                  }}
                >
                  <Email sx={{ fontSize: { xs: 18, md: 20 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Email
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
                <IconButton
                  className="action-icon"
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    transition: "all 0.3s ease",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Launch sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MallDetails;
